// 작전 구성의 defense 탭, **등록 순 시퀀스와 행 편집**
//
// 왼쪽에서 관측을 넣고 오른쪽에서 그것이 등록한 순서 그대로 서는 것을 본다. attack 탭이 좌열
// 폼과 우열 미니 시퀀스를 같은 상태 위에 묶은 것과 같은 모양이고, 이유도 같다 — 둘이 각자
// 서버를 읽으면 등록 직후 한 순간에 좌열은 새 줄을 알고 우열은 모르는 상태가 생긴다.
//
// ── 계산을 여기서 하지 않는다 ───────────────────────────────────────────────
//
// 착탄 역산도 정렬도 `src/web/defense/derive.ts` 가 지고, 그 파일은 다시
// `src/core/timing/defense.ts` 를 부르기만 한다. 이 화면이 「여기서 한 줄 빼면 되잖아」로
// 숫자를 만들기 시작하면 δ 의 부호가 두 곳에 살고, 코어의 검사 스물일곱은 그 두 번째 벌을
// 한 글자도 지키지 못한다. `tests/unit/defense/derive.test.ts` 가 그 규율을 기계로 붙든다.
//
// ── dispatch 가 없다 ────────────────────────────────────────────────────────
//
// 공격에는 조립을 마치고 시각을 확정하는 단계가 있지만 수비에는 없다. 적이 이미 날아오는
// 중이라 확정할 것이 없고, **등록·재앵커·삭제가 전부 즉시 전 접속자에게 나간다.** 그래서
// 이 화면에는 「마치기」도 「내보내기」도 없다.
//
// ── 무장과 편집이 부모에 사는 이유 ──────────────────────────────────────────
//
// 줄마다 자기 무장 상태를 들면 A 를 무장하고 B 를 무장했을 때 **「한 번 더 누르면 지웁니다」가
// 둘 떠 있는데 무장된 줄은 하나도 없는** 상태가 만들어진다. 명부 화면이 2026-08-14 대리
// 관측에서 정확히 그것으로 1점을 깎였고, 뿌리는 문장을 상태에서 파생시키지 않고 저장한
// 것이었다. 여기서는 **무장한 줄이 하나뿐**이라는 것을 자료 구조가 강제한다.
//
// ── 흐르는 시각을 새로 만들지 않는다 ────────────────────────────────────────
//
// 0.1초 칸으로 버리는 규칙이 이미 열람 화면의 `live-clock.ts` 에 있고 그것을 **읽는다.**
// 세 번째 벌을 만들면 같은 4.99초가 화면마다 다른 글자가 되고, 그 차이는 아무도 못 본다.
// 이 import 는 공격 갈래를 **고치지 않는다** — 되돌릴 때 수비만 지우면 공격은 그대로 산다.

import { useCallback, useMemo, useState } from 'react'

// 계약의 `EnemyPhase` 는 TypeBox 값이라 타입 자리에 쓸 수 없다. 단계의 타입은 코어의 것이다.
import type { EnemyPhase } from '../../../../core/timing/defense.ts'
import {
  common as commonStrings,
  type CommonStrings,
  defense as defenseStrings,
  type DefenseStrings,
  shell as shellStrings,
} from '../../../../i18n/index.ts'
import { CT_TOKEN_KEY, readToken } from '../../../access/client.ts'
import { type EnemyLine, enemyLines } from '../../../defense/derive.ts'
import { useDefense } from '../../../defense/use-defense.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { useShellClock } from '../../../shell/use-shell-clock.ts'
import { Figures } from '../../../ui/figures.tsx'
import { parseTimeText, TimeInput, timeParseMessage } from '../../../ui/time-input.tsx'
import { formatRemaining, formatSinceOpen } from '../../view-attack/live-clock.ts'
import { RallyFormDefense } from '../rally/form-defense.tsx'
import { IconTile } from '../../../ui/icons.tsx'
import { LabelHint } from '../../../ui/label-hint.tsx'

/**
 * 0.1초 칸이 흐르려면 그보다 잦게 그려야 한다. 옵션 객체를 모듈 상수로 두는 이유는 렌더마다
 * 새 객체를 만들면 시계 훅의 효과가 매번 다시 도는 자리가 생기기 때문이다.
 */
const CLOCK = { tickMs: 100 } as const

/** 지금 고른 언어의 문자열 한 벌. **모듈에 굳히지 않는다** — 굳히면 언어를 바꿔도 안 따라온다. */
function useStrings(): DefenseStrings {
  return defenseStrings(useLang().lang)
}

/** 시간 입력 안내와 거절은 여러 화면이 같은 글자를 내야 해서 공용 네임스페이스가 진다. */
function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

/** 남은 시간을 글자로. **지나간 것을 「앞으로 올 것」이라고 말하지 않는다.** */
function liveText(millis: number): string {
  return millis >= 0 ? formatRemaining(millis) : formatSinceOpen(-millis)
}

/**
 * 지금 무엇을 하면 되는지 한 줄. **공격 갈래의 조각을 빌리지 않고 여기 적는다** — 두 갈래가
 * 공유하는 컴포넌트가 없다는 것이 이 스텝이 공격과 나란히 돌 수 있는 근거이고, 세 줄짜리
 * 마크업을 아끼자고 그 근거를 버리지 않는다.
 */
function NextStep(props: { text: string }): React.ReactElement {
  return (
    <p data-testid="defense-next" className="note note-accent mt-group text-sm">
      {props.text}
    </p>
  )
}

export function DefenseView(): React.ReactElement {
  const [ctToken] = useState<string | null>(() => readToken(CT_TOKEN_KEY))
  const store = useDefense(ctToken)
  const clock = useShellClock(CLOCK)
  const t = useStrings()
  const { lang } = useLang()

  const [armedId, setArmedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [done, setDone] = useState('')

  // 연맹원의 기본 탭은 이 탭이 아니라 구성 화면 껍데기가 적는다. 탭을 **열 때** 적어야 하는데,
  // 여기서는 떠날 때 되돌리는 정리 함수만 쓸 수 있었고 브라우저를 닫으면 그것이 돌지 않았다
  // (`src/web/screens/compose/shell/index.tsx` 의 기본 탭 효과, T2 개정 둘째).

  const lines = useMemo(
    () => enemyLines(store.enemyRallies, store.rosters, clock.utcMillis),
    [store.enemyRallies, store.rosters, clock.utcMillis],
  )
  const enemies = useMemo(() => store.rosters.filter((row) => row.side === 'enemy'), [store.rosters])

  // **착탄 미상은 맨 아래 흐린 띠에 모인다** (2026-08-15 사용자 결정). 파생이 이미 미상을 맨
  // 뒤로 보내고 있어 여기서는 띠를 그릴 자리만 가른다. 순서를 다시 정하지 않는다.
  const known = lines.filter((line) => line.impact !== undefined)
  const unknown = lines.filter((line) => line.impact === undefined)

  const remove = useCallback(
    async (rallyId: string): Promise<void> => {
      setDone('')
      const error = await store.removeEnemyRally(rallyId)
      setMessage(error ?? '')
      if (error === null) setArmedId(null)
    },
    [store],
  )

  const reanchor = useCallback(
    async (rallyId: string, draft: { phase: EnemyPhase; remainingMs: number }): Promise<boolean> => {
      const error = await store.reanchor(rallyId, draft)
      setMessage(error ?? '')
      if (error !== null) return false
      setDone(t.rowReanchorDone)
      setEditingId(null)
      return true
    },
    [store, t.rowReanchorDone],
  )

  return (
    // 딥틸 배경은 2026-08-26 사용자 지시로 없앴다(열람 화면은 전날). 「지금 보고 있는 것이
    // 무엇인가」는 배경이 아니라 탭과 제목이 말한다. 정본 7.8 의 같은 문장도 함께 개정했다.
    <section
      data-testid="defense-view"
      data-loaded={String(store.loaded)}
      className="min-w-0 py-group"
      aria-label={t.composeTitle}
    >
      {/* 제목은 라벨(defense)과 뜻풀이(적 rally 시퀀스) 두 요소다(2026-10-14 사용자 지시, 대시로 잇지 않는다). */}
      <h2 className="section-title flex items-center gap-2.5">
        <IconTile name="defense" tone="cyan" size="sm" />
        <LabelHint label={t.composeTitle} hint={t.composeTitleHint} />
      </h2>
      <p data-testid="defense-why" className="mt-line text-xs text-ink-mute">
        {t.composeWhy}
      </p>

      {/* 껍데기와 값을 가른다. 값이 오기 전에 빈 목록을 그리면 「적이 없다」와 「아직 못
          읽었다」가 같은 화면이 되고, 브라우저 검사가 그 둘을 구별하지 못해 회차마다 다른
          시나리오가 죽는다. */}
      {!store.loaded ? (
        <p className="mt-group text-sm text-ink-mute">{t.composeTitle}</p>
      ) : (
        <>
          <NextStep text={lines.length === 0 ? t.composeNextRegister : t.composeNextWatch} />

          <div className="mt-block grid min-w-0 gap-6 md:grid-cols-2">
            <RallyFormDefense enemies={enemies} onAdd={store.addEnemyRally} />

            <div className="card min-w-0">
              {/* 왼쪽 등록 패널과 같은 머리 띠(2026-10-13, SaaS 비평의 지적, 세션 판단). 글자는 새로 쓰지 않고 고정줄 아래층의 수비 요약
                  「적 rally N」을 그대로 쓴다. 두 자리가 같은 개수(미상 포함 전부)를 같은 말로 센다. */}
              <div data-testid="defense-list-head" className="panel-head cat-cyan">
                <IconTile name="layers" tone="cyan" />
                <h3 className="section-title">
                  {shellStrings(lang).bandDefenseCount.replaceAll('{n}', String(lines.length))}
                </h3>
              </div>
              {/* 0 건일 때 머리 「적 rally 0」과 아래 문장이 같은 사실을 두 번 말한다(앞 회차 리뷰, 사소). 머리의 개수는 그대로 둔다(세션 판단,
                  2026-10-13): 머리는 고정줄 아래층 요약과 같은 글자를 쓰는 것이 약속이라 0 에서만 숫자를 빼면 두 자리가 갈리고, 머리 모양이
                  건수에 따라 바뀐다. 문장은 빈 패널이 고장이 아니라 비었다는 것을 말로 알리는 자리라 남긴다. */}
              {lines.length === 0 ? (
                <p data-testid="defense-empty" className="text-sm text-ink-mute">
                  {t.composeEmpty}
                </p>
              ) : (
                <ul data-testid="defense-list" className="divide-y divide-line-hair">
                  {known.map((line) => (
                    <EnemyRow
                      key={line.rally.id}
                      line={line}
                      t={t}
                      nowMs={clock.utcMillis}
                      dim={false}
                      armed={armedId === line.rally.id}
                      editing={editingId === line.rally.id}
                      onArm={setArmedId}
                      onEdit={setEditingId}
                      onRemove={remove}
                      onReanchor={reanchor}
                    />
                  ))}
                </ul>
              )}

              {/* ── 착탄 미상 띠 ───────────────────────────────────────────
                  계산되지 않은 줄을 계산된 줄과 한 목록에 섞지 않는다(2026-08-15 사용자
                  결정, 이유는 2026-08-26 에 바뀌었다). 명부에 marching 이 들어오면 그 줄은
                  등록 순서의 제자리로 들어간다. */}
              {unknown.length > 0 && (
                <section
                  data-testid="defense-unknown-band"
                  data-count={String(unknown.length)}
                  className="note mt-block text-ink-dim"
                  aria-label={t.unknownBand}
                >
                  <h3 className="section-title caps">{t.unknownBand}</h3>
                  <p className="mt-line text-xs text-ink-dim">{t.unknownWhy}</p>
                  <ul className="mt-group divide-y divide-line-hair">
                    {unknown.map((line) => (
                      <EnemyRow
                        key={line.rally.id}
                        line={line}
                        t={t}
                        nowMs={clock.utcMillis}
                        dim
                        armed={armedId === line.rally.id}
                        editing={editingId === line.rally.id}
                        onArm={setArmedId}
                        onEdit={setEditingId}
                        onRemove={remove}
                        onReanchor={reanchor}
                      />
                    ))}
                  </ul>
                  <p className="mt-group text-xs text-ink-dim">{t.unknownFix}</p>
                </section>
              )}

              {message !== '' && (
                <p
                  data-testid="defense-list-message"
                  role="alert"
                  className="mt-group text-sm text-red-300"
                >
                  {message}
                </p>
              )}
              {done !== '' && (
                <p data-testid="defense-list-done" className="mt-group text-sm text-cyan">
                  {done}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  )
}

interface EnemyRowProps {
  line: EnemyLine
  t: DefenseStrings
  nowMs: number
  dim: boolean
  armed: boolean
  editing: boolean
  onArm: (rallyId: string | null) => void
  onEdit: (rallyId: string | null) => void
  onRemove: (rallyId: string) => Promise<void>
  onReanchor: (
    rallyId: string,
    draft: { phase: EnemyPhase; remainingMs: number },
  ) => Promise<boolean>
}

/**
 * 적 rally 한 줄.
 *
 * **재앵커는 값 갱신이 아니라 새 기준을 찍는 일이다.** 그래서 이 패널은 「고치기」가 아니라
 * 「다시 앵커」라고 부르고, 단계도 함께 받는다 — rallying 으로 앵커해 둔 행을 행군 중에 다시
 * 앵커하면 식에서 적 marching 항이 빠져 **명부 오차가 그 행에서 사라진다.** 그것이 이 문의
 * 존재 이유의 절반이다.
 */
function EnemyRow(props: EnemyRowProps): React.ReactElement {
  const { line, t } = props
  const c = useCommon()
  const [phase, setPhase] = useState<EnemyPhase>(line.rally.phase)
  const [remaining, setRemaining] = useState('')
  const [timeMessage, setTimeMessage] = useState('')

  const submit = async (): Promise<void> => {
    const parsed = parseTimeText(remaining)
    if (!parsed.ok) {
      setTimeMessage(timeParseMessage(parsed.reason, c))
      return
    }
    setTimeMessage('')
    const ok = await props.onReanchor(line.rally.id, { phase, remainingMs: parsed.millis })
    if (ok) setRemaining('')
  }

  const liveLabel =
    line.stage === 'rallying'
      ? t.rowRallyingLeft
      : line.liveRemainingMs !== null && line.liveRemainingMs < 0
        ? t.rowImpactPassed
        : t.rowImpactLeft

  // **`data-anchored-at` 은 「재앵커가 반영됐다」를 관측 가능하게 만드는 자리다.** 재앵커의
  // 성질이 「목록의 모양이 거의 안 바뀐다」라서, 반영된 회차와 아무 일도 없는 회차가 흐르는
  // 숫자 말고는 글자 하나 다르지 않다. 전파 시간을 재려면 **「반영됐다」에 해당하는 관측
  // 가능한 순간**이 있어야 하고, 없으면 재는 배선이 왕복만 재고 화면이 다시 그려지는 시간을
  // 빠뜨린다. 재구성 스텝의 `data-retime-count` 가 같은 이유로 회차를 세고 있다.
  return (
    <li
      data-testid="enemy-row"
      data-rally-id={line.rally.id}
      data-impact-known={String(line.impact !== undefined)}
      data-stage={line.stage}
      data-anchored-at={String(line.rally.anchoredAt)}
      className={`min-w-0 py-group text-base sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end sm:gap-x-3 ${props.dim ? 'text-ink-dim' : ''}`}
    >
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-2 gap-y-line sm:col-span-2">
        {/* 이름을 못 찾으면 **식별자를 뱉지 않는다.** 사람이 읽는 자리에 내부 id 가 뜨면
            그것은 이름이 아니라 고장으로 읽히고, 무엇보다 그 줄이 누구 것인지는 여전히
            모른다. 모르면 모른다고 말하는 편이 낫다. */}
        <span data-testid="enemy-name" className="min-w-0 truncate text-sm font-semibold">
          {line.enemyName ?? t.rowEnemyGone}
        </span>
        <span
          data-testid="enemy-anchor"
          className="chip shrink-0"
        >
          {line.rally.phase === 'rallying' ? t.rowAnchoredRallying : t.rowAnchoredMarching}
        </span>
      </div>

      <Figures
        at="lg"
        wide="inline"
        className="mt-line"
        figures={[
          {
            label: liveLabel,
            value: line.liveRemainingMs === null ? t.unknownBand : liveText(line.liveRemainingMs),
            testId: 'enemy-live',
          },
        ]}
      />

      <div className="mt-line flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="enemy-reanchor"
          aria-expanded={props.editing}
          className="btn btn-secondary"
          onClick={() => {
            props.onEdit(props.editing ? null : line.rally.id)
            props.onArm(null)
          }}
        >
          {props.editing ? t.rowCancel : t.rowReanchor}
        </button>
        <button
          type="button"
          data-testid="enemy-remove"
          className={props.armed ? 'btn btn-danger-armed' : 'btn btn-ghost text-red-300'}
          onClick={() => {
            if (props.armed) {
              void props.onRemove(line.rally.id)
              return
            }
            props.onArm(line.rally.id)
          }}
        >
          {props.armed ? t.rowRemoveArmed : t.rowRemove}
        </button>
      </div>

      {/* **경고문을 저장하지 않고 무장 상태에서 파생시킨다.** 저장하면 다른 줄을 무장했을
          때 앞 줄의 문장이 남아, 「한 번 더 누르면 지웁니다」가 둘 떠 있는데 무장된 줄은
          하나도 없는 상태가 만들어진다. 명부 화면이 그것으로 값을 깎였다. */}
      {props.armed && (
        <p data-testid="enemy-remove-what" role="alert" className="mt-line text-xs text-red-300 sm:col-span-2">
          {t.rowRemoveWhat}
        </p>
      )}

      {props.editing && (
        <div data-testid="enemy-reanchor-panel" className="note mt-group sm:col-span-2">
          <p className="text-xs text-ink-dim">{t.rowReanchorWhy}</p>
          <fieldset className="mt-group">
            <legend className="text-xs font-medium text-ink-dim">{t.formPhase}</legend>
            <div className="mt-line flex flex-wrap gap-3">
              {(['rallying', 'marching'] as const).map((candidate) => (
                <label key={candidate} className="flex items-center gap-1.5 py-control text-sm">
                  <input
                    type="radio"
                    className="size-5"
                    name={`reanchor-phase-${line.rally.id}`}
                    data-testid={`enemy-reanchor-phase-${candidate}`}
                    checked={phase === candidate}
                    onChange={() => {
                      setPhase(candidate)
                    }}
                  />
                  <span>{candidate === 'rallying' ? t.formPhaseRallying : t.formPhaseMarching}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="mt-group">
            <TimeInput
              testId="enemy-reanchor-remaining"
              label={t.formRemaining}
              value={remaining}
              message={timeMessage}
              compact
              onChange={(next) => {
                setRemaining(next)
                setTimeMessage('')
              }}
            />
          </div>
          <button
            type="button"
            data-testid="enemy-reanchor-submit"
            className="btn btn-secondary mt-group"
            onClick={() => void submit()}
          >
            {t.rowReanchor}
          </button>
        </div>
      )}
    </li>
  )
}
